/**
 * Modal pour ajouter plusieurs charges fixes en bulk
 * @module components/tresorerie/BulkChargesFixesModal
 */

import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, AlertCircle, Home, Shield, Car, Building2 } from 'lucide-react';
import { BulkChargeFixeData, EMPTY_CHARGE_FIXE, CHARGE_FIXE_TEMPLATES } from '@/types/bulk';
import { useBulkDraft } from '@/hooks/useBulkDraft';
import { BulkChargesFixesRow } from './BulkChargesFixesRow';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { CreateChargeFixData } from '@/types/charges-fixes.types';
import { useModalProtection } from '@/hooks/useModalProtection';

interface BulkChargesFixesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entrepriseId: string;
  onSubmit: (data: CreateChargeFixData[]) => Promise<boolean>;
  isLoading?: boolean;
}

export function BulkChargesFixesModal({
  open,
  onOpenChange,
  entrepriseId,
  onSubmit,
  isLoading = false,
}: BulkChargesFixesModalProps) {
  useModalProtection(open);

  const { draft, setDraft, clearDraft, hasPendingDraft, restoreDraft } = useBulkDraft<BulkChargeFixeData>({
    key: 'bulk_charges_fixes',
    entrepriseId,
  });

  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  // Initialiser avec une ligne vide à l'ouverture
  useEffect(() => {
    if (open) {
      if (hasPendingDraft) {
        setShowRestorePrompt(true);
      } else {
        setDraft([createEmptyRow()]);
      }
    }
  }, [open, hasPendingDraft]);

  const createEmptyRow = (): BulkChargeFixeData => ({
    id: uuidv4(),
    ...EMPTY_CHARGE_FIXE,
  });

  const createFromTemplate = (template: keyof typeof CHARGE_FIXE_TEMPLATES): BulkChargeFixeData => ({
    id: uuidv4(),
    ...CHARGE_FIXE_TEMPLATES[template],
  });

  const handleRestore = () => {
    const restored = restoreDraft();
    if (!restored || restored.length === 0) {
      setDraft([createEmptyRow()]);
    }
    setShowRestorePrompt(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraft([createEmptyRow()]);
    setShowRestorePrompt(false);
  };

  const handleChange = (index: number, data: Partial<BulkChargeFixeData>) => {
    const updated = [...draft];
    updated[index] = { ...updated[index], ...data };
    setDraft(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      Object.keys(data).forEach(key => {
        delete newErrors[index]?.[key];
      });
      setErrors(newErrors);
    }
  };

  const handleAdd = () => {
    setDraft([...draft, createEmptyRow()]);
  };

  const handleAddTemplate = (template: keyof typeof CHARGE_FIXE_TEMPLATES) => {
    setDraft([...draft, createFromTemplate(template)]);
  };

  const handleDuplicate = (index: number) => {
    const source = draft[index];
    const duplicated: BulkChargeFixeData = {
      id: uuidv4(),
      nom: '', // Vider le nom pour forcer la saisie
      categorie: source.categorie,
      montant_ht: source.montant_ht,
      taux_tva: source.taux_tva,
      frequence: source.frequence,
      jour_prelevement: source.jour_prelevement,
    };
    const updated = [...draft];
    updated.splice(index + 1, 0, duplicated);
    setDraft(updated);
  };

  const handleDelete = (index: number) => {
    if (draft.length <= 1) return;
    const updated = draft.filter((_, i) => i !== index);
    setDraft(updated);
  };

  const validate = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let isValid = true;

    draft.forEach((row, index) => {
      const rowErrors: Record<string, string> = {};

      if (!row.nom.trim()) {
        rowErrors.nom = 'Requis';
        isValid = false;
      }

      if (!row.montant_ht || row.montant_ht <= 0) {
        rowErrors.montant_ht = 'Requis';
        isValid = false;
      }

      if (!row.jour_prelevement || row.jour_prelevement < 1 || row.jour_prelevement > 31) {
        rowErrors.jour_prelevement = '1-31';
        isValid = false;
      }

      if (Object.keys(rowErrors).length > 0) {
        newErrors[index] = rowErrors;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Préparer les données sans l'id temporaire
    const validRows = draft
      .filter(row => row.nom.trim())
      .map(({ id, ...rest }) => rest as CreateChargeFixData);

    const success = await onSubmit(validRows);

    if (success) {
      clearDraft();
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const validRowsCount = draft.filter(row => row.nom.trim()).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Ajouter des charges fixes</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Ajoutez vos dépenses récurrentes (loyer, assurance, leasing, etc.)
          </p>
        </DialogHeader>

        {/* Prompt de restauration de brouillon */}
        {showRestorePrompt && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Un brouillon non sauvegardé a été trouvé. Voulez-vous le restaurer ?</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleDiscardDraft}>
                  Non, recommencer
                </Button>
                <Button size="sm" onClick={handleRestore}>
                  Oui, restaurer
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Templates rapides */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground self-center">Templates :</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('loyer')}
          >
            <Home className="h-4 w-4 mr-1" />
            + Loyer (1500€)
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('assurance')}
          >
            <Shield className="h-4 w-4 mr-1" />
            + Assurance (300€)
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('leasing')}
          >
            <Car className="h-4 w-4 mr-1" />
            + Leasing (500€)
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('comptable')}
          >
            <Building2 className="h-4 w-4 mr-1" />
            + Comptable (400€)
          </Button>
        </div>

        {/* En-têtes des colonnes */}
        <div className="grid grid-cols-[1fr_160px_120px_80px_120px_80px_auto] gap-3 px-3 text-xs text-muted-foreground font-medium">
          <span>Nom *</span>
          <span>Catégorie</span>
          <span>Montant HT *</span>
          <span>TVA %</span>
          <span>Fréquence</span>
          <span>Jour *</span>
          <span>Actions</span>
        </div>

        {/* Liste des charges */}
        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {draft.map((row, index) => (
            <BulkChargesFixesRow
              key={row.id}
              index={index}
              data={row}
              onChange={handleChange}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              canDelete={draft.length > 1}
              errors={errors[index] || {}}
            />
          ))}
        </div>

        {/* Bouton ajouter */}
        <Button
          type="button"
          variant="outline"
          onClick={handleAdd}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une ligne
        </Button>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || validRowsCount === 0}
          >
            {isLoading ? 'Ajout...' : `Ajouter ${validRowsCount} charge(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
